import React, { useState, useCallback, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Typography, Box, LinearProgress, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { Alert } from "@mui/material";
import {
    CloudUpload as CloudUploadIcon,
    Description as DescriptionIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import { VCard } from "../../../../core/components/VCard";
import { PrimaryButton, SecondaryButton } from "../../../layout/Button/Button";
import { uploadFile, ValidationResult } from "../../../../client/api/branchedStory";

const useStyles = makeStyles((theme) => ({
    uploadArea: {
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(6), // 48px
        textAlign: "center",
        cursor: "pointer",
        transition: theme.transitions.create(["border-color", "background-color"]),
        "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
        },
    },
    dragActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light + "10",
    },
    hiddenInput: {
        display: "none",
    },
    uploadIcon: {
        fontSize: "4rem",
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2), // 16px
    },
    uploadText: {
        marginBottom: theme.spacing(1), // 8px
        fontWeight: 500,
    },
    uploadHint: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2), // 16px
    },
    fileInfo: {
        marginTop: theme.spacing(3), // 24px
    },
    fileName: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1), // 8px
        marginBottom: theme.spacing(2), // 16px
    },
    validationResults: {
        marginTop: theme.spacing(3), // 24px
    },
    validationItem: {
        paddingLeft: 0,
        paddingRight: 0,
    },
    actions: {
        marginTop: theme.spacing(3), // 24px
        display: "flex",
        gap: theme.spacing(2), // 16px
        justifyContent: "flex-end",
    },
    progressContainer: {
        marginTop: theme.spacing(2), // 16px
    },
}));

interface UploadBranchedStoryProps {
    onFileUploaded?: (result: {
        storyId?: number;
        validation: ValidationResult;
        parsedStory?: import("../../../../client/api/branchedStory").ParsedStoryStructure;
    }) => void;
    onComplete?: () => void;
}

export const UploadBranchedStory: React.FC<UploadBranchedStoryProps> = ({ onFileUploaded, onComplete }) => {
    const classes = useStyles();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
    const [uploadResult, setUploadResult] = useState<{ storyId?: number } | null>(null);
    const [parsedStory, setParsedStory] = useState<
        import("../../../../client/api/branchedStory").ParsedStoryStructure | null
    >(null);
    const [error, setError] = useState<string | null>(null);

    const handleFileUpload = useCallback(
        async (file: File) => {
            // Validate file type - accept .docx files only
            const isDocx = file.name.toLowerCase().endsWith(".docx");

            if (!isDocx) {
                setError("Please upload a Word (.docx) file");
                return;
            }

            setUploadedFile(file);
            setError(null);
            setUploading(true);
            setUploadProgress(0);

            try {
                // Simulate progress for better UX
                const progressInterval = setInterval(() => {
                    setUploadProgress((prev) => Math.min(prev + 10, 90));
                }, 100);

                // V5 DOCX upload - use multipart FormData
                const response = await uploadFile(file);

                clearInterval(progressInterval);
                setUploadProgress(100);

                if (response.success) {
                    // Set uploadResult to indicate successful upload (storyId will be undefined until final save)
                    setUploadResult({ storyId: response.storyId });
                    setValidationResult(response.validationResult);
                    setParsedStory(response.parsedStory || null);

                    console.log("=== UploadBranchedStory: Upload successful ===");
                    console.log("storyId:", response.storyId);
                    console.log("validation:", response.validationResult);
                    console.log("validation.valid:", response.validationResult?.valid);
                    console.log("parsedStory:", response.parsedStory);

                    if (onFileUploaded) {
                        console.log("=== UploadBranchedStory: Calling onFileUploaded ===");
                        onFileUploaded({
                            storyId: response.storyId,
                            validation: response.validationResult,
                            parsedStory: response.parsedStory,
                        });
                    }
                } else {
                    setError(response.error || "Upload failed");
                    setValidationResult(response.validationResult);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : "Upload failed");
            } finally {
                setUploading(false);
            }
        },
        [onFileUploaded],
    );

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileUpload(file);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleTryAgain = () => {
        setUploadedFile(null);
        setValidationResult(null);
        setUploadResult(null);
        setParsedStory(null);
        setError(null);
        setUploadProgress(0);
    };

    const handleContinue = () => {
        if (onComplete) {
            onComplete();
        }
    };

    const renderValidationResults = () => {
        if (!validationResult) return null;

        const { valid, errors, warnings, stats } = validationResult;

        return (
            <VCard className={classes.validationResults}>
                <Typography variant="h6" gutterBottom>
                    Validation Results
                </Typography>

                {valid ? (
                    <Alert severity="success" style={{ marginBottom: 16 }}>
                        Story structure is valid and ready to use!
                    </Alert>
                ) : (
                    <Alert severity="error" style={{ marginBottom: 16 }}>
                        Story validation failed. Please fix the errors below.
                    </Alert>
                )}

                {/* Story Statistics */}
                <Box marginBottom={2}>
                    <Typography variant="subtitle2" gutterBottom>
                        Story Statistics
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Parts: {stats.totalParts} | Decision Points: {stats.totalDecisionPoints} | Branches:{" "}
                        {stats.totalBranches} | Max Depth: {stats.maxDepth} | End Points: {stats.endPoints}
                    </Typography>
                </Box>

                {/* Per-Part Breakdown */}
                {parsedStory && parsedStory.parts && parsedStory.parts.length > 0 && (
                    <Box marginBottom={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            Section Breakdown
                        </Typography>
                        <List dense>
                            {parsedStory.parts.map((part) => (
                                <ListItem key={part.partNumber} className={classes.validationItem}>
                                    <ListItemIcon>
                                        <HelpOutlineIcon
                                            color={part.questions && part.questions.length > 0 ? "primary" : "disabled"}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`Section ${part.partNumber}${part.partLabel ? `: ${part.partLabel}` : ""}`}
                                        secondary={`${part.questions?.length || 0} question(s) | ${part.decisionPoints?.length || 0} choice(s)`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {/* Errors */}
                {errors.length > 0 && (
                    <List dense>
                        {errors.map((error, index) => (
                            <ListItem key={index} className={classes.validationItem}>
                                <ListItemIcon>
                                    <ErrorIcon color="error" />
                                </ListItemIcon>
                                <ListItemText primary={error.message} primaryTypographyProps={{ variant: "body2" }} />
                            </ListItem>
                        ))}
                    </List>
                )}

                {/* Warnings */}
                {warnings.length > 0 && (
                    <List dense>
                        {warnings.map((warning, index) => (
                            <ListItem key={index} className={classes.validationItem}>
                                <ListItemIcon>
                                    <WarningIcon style={{ color: "#ff9800" }} />
                                </ListItemIcon>
                                <ListItemText primary={warning.message} primaryTypographyProps={{ variant: "body2" }} />
                            </ListItem>
                        ))}
                    </List>
                )}
            </VCard>
        );
    };

    return (
        <div>
            {!uploadedFile && (
                <div className={classes.uploadArea} onClick={handleUploadClick}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".docx"
                        onChange={handleFileInputChange}
                        className={classes.hiddenInput}
                    />
                    <CloudUploadIcon className={classes.uploadIcon} />
                    <Typography variant="h6" className={classes.uploadText}>
                        Upload Branched Story
                    </Typography>
                    <Typography variant="body2" className={classes.uploadHint}>
                        Click to select a Word (.docx) file
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Upload Branched Story in .DOCX file format prepared with v5 template
                    </Typography>
                </div>
            )}

            {uploadedFile && (
                <VCard className={classes.fileInfo}>
                    <div className={classes.fileName}>
                        <DescriptionIcon color="primary" />
                        <Typography variant="body1" style={{ fontWeight: 500 }}>
                            {uploadedFile.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            ({(uploadedFile.size / 1024).toFixed(1)} KB)
                        </Typography>
                    </div>

                    {uploading && (
                        <div className={classes.progressContainer}>
                            <Typography variant="body2" gutterBottom>
                                Processing story...
                            </Typography>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                        </div>
                    )}

                    {error && (
                        <Alert severity="error" style={{ marginTop: 16 }}>
                            {error}
                        </Alert>
                    )}
                </VCard>
            )}

            {renderValidationResults()}

            {(uploadedFile || error) && (
                <div className={classes.actions}>
                    <SecondaryButton onClick={handleTryAgain}>
                        {error ? "Try Again" : "Upload Different File"}
                    </SecondaryButton>
                    {uploadResult && validationResult && (
                        <PrimaryButton onClick={handleContinue} startIcon={<CheckCircleIcon />}>
                            Continue to Story Details
                        </PrimaryButton>
                    )}
                </div>
            )}
        </div>
    );
};
